import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { GitMerge, ArrowRight, AlertTriangle, Loader2 } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

/**
 * Super Admin: merge a duplicate account into the surviving one.
 * Flow: pick target → Preview (dry run counts) → type MERGE → Execute.
 */
const MergeAccountsDialog = ({ open, onClose, sourceUser, users, getAuthConfig, onMerged }) => {
  const [targetId, setTargetId] = useState("");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (users || [])
      .filter((u) => u.user_id !== sourceUser?.user_id && !u.parent_user_id && !u.is_admin && u.account_status !== "merged")
      .filter((u) => !q || (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q) || (u.company_name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [users, sourceUser, search]);

  const target = (users || []).find((u) => u.user_id === targetId);

  const reset = () => { setTargetId(""); setSearch(""); setPreview(null); setConfirmText(""); };

  const runPreview = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/accounts/merge/preview`,
        { source_user_id: sourceUser.user_id, target_user_id: targetId }, getAuthConfig());
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const runMerge = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/accounts/merge`,
        { source_user_id: sourceUser.user_id, target_user_id: targetId, confirm_text: confirmText }, getAuthConfig());
      toast.success(`Merged — ${res.data.total_moved} documents moved${res.data.total_parked ? `, ${res.data.total_parked} settings parked` : ""}`);
      reset(); onClose(); onMerged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  };

  if (!sourceUser) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg bg-card border-border" data-testid="merge-accounts-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <GitMerge className="w-4 h-4 text-purple-500" /> Merge Duplicate Account
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <div className="flex-1 rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2 min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-red-500 font-semibold">Duplicate (emptied)</p>
              <p className="text-foreground font-medium truncate">{sourceUser.name || sourceUser.email}</p>
              <p className="text-muted-foreground text-xs truncate">{sourceUser.email}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="flex-1 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-emerald-500 font-semibold">Keeps everything</p>
              {target ? (
                <>
                  <p className="text-foreground font-medium truncate">{target.name || target.email}</p>
                  <p className="text-muted-foreground text-xs truncate">{target.email}</p>
                </>
              ) : (
                <p className="text-muted-foreground text-xs">Select the account to keep below</p>
              )}
            </div>
          </div>

          {!preview && (
            <div className="space-y-2">
              <Input
                placeholder="Search accounts by email, name or company…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setTargetId(""); }}
                className="bg-background border-border h-9 text-sm"
                data-testid="merge-target-search"
              />
              <div className="rounded-md border border-border divide-y divide-border/60 max-h-48 overflow-y-auto">
                {candidates.length === 0 ? (
                  <p className="text-xs text-muted-foreground p-3">No matching accounts</p>
                ) : candidates.map((u) => (
                  <button key={u.user_id} type="button"
                    onClick={() => setTargetId(u.user_id)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-muted/50 ${targetId === u.user_id ? "bg-emerald-500/10" : ""}`}
                    data-testid={`merge-target-option-${u.user_id}`}>
                    <span className="min-w-0">
                      <span className="block text-foreground truncate">{u.name || "—"} {u.company_name ? `· ${u.company_name}` : ""}</span>
                      <span className="block text-xs text-muted-foreground truncate">{u.email}</span>
                    </span>
                    {targetId === u.user_id && <span className="text-emerald-500 text-xs font-semibold shrink-0">Keep</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {preview && (
            <div className="space-y-2" data-testid="merge-preview">
              <p className="text-xs text-muted-foreground">
                <span className="text-foreground font-semibold">{preview.total_documents}</span> documents will move
                {preview.team_logins > 0 && <> · <span className="text-foreground font-semibold">{preview.team_logins}</span> team login(s) re-assigned</>}
              </p>
              <div className="rounded-md border border-border max-h-44 overflow-y-auto">
                {Object.entries(preview.collections).map(([name, info]) => (
                  <div key={name} className="flex items-center justify-between px-3 py-1.5 text-xs border-b border-border/50 last:border-0">
                    <span className="text-foreground font-mono">{name}</span>
                    <span className={info.mode === "park" ? "text-amber-500" : "text-muted-foreground"}>
                      {info.count} {info.mode === "park" ? "(kept: target's own settings)" : ""}
                    </span>
                  </div>
                ))}
              </div>
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 flex gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-[11px] text-muted-foreground">
                  This cannot be undone. The duplicate's login is permanently disabled and all its jobs, customers,
                  invoices, numbers and history move to the kept account. Type <span className="font-semibold text-foreground">MERGE</span> to confirm.
                </p>
              </div>
              <Input
                placeholder='Type "MERGE" to confirm'
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="bg-background border-border h-9 text-sm"
                data-testid="merge-confirm-input"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { reset(); onClose(); }} className="border-border" data-testid="merge-cancel-btn">Cancel</Button>
          {!preview ? (
            <Button onClick={runPreview} disabled={!targetId || busy} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="merge-preview-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview Merge"}
            </Button>
          ) : (
            <Button onClick={runMerge} disabled={confirmText.trim().toUpperCase() !== "MERGE" || busy}
              className="bg-red-600 hover:bg-red-700 text-white" data-testid="merge-execute-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Merge Accounts"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MergeAccountsDialog;
